import { createSelector } from 'reselect'
import { get, orderBy, sumBy, values, filter, map, isFinite, take, isEmpty } from 'lodash'
import moment from 'moment'

import { makeGetAttendantMenuTypes, makeGetAttendantStandUuids } from './attendant'
import { getCurrentEventId } from './events'
import { getShowClosedTabs, getShowOnlyActiveAttendantOrders } from './search'
import { getCurrentMenuId } from './appState'
import { getActiveAttendantId } from './attendant'

import OrderStates, { ORDER_STATES } from '../utils/orderStates'
import PaymentTypes from '../utils/paymentTypes'

export const getPurgedAt = (state) => get(state, 'order.purgedAt', undefined)

export const getPurgableOrders = (state, msThreshold) => {
  const ordersById = get(state, 'order.byId', {})
  const isPurgable = (order) => moment().diff(moment(order.syncedAt)) >= msThreshold
  const syncedOrdersIds = map(filter(values(ordersById), isPurgable), 'uuid')

  return syncedOrdersIds
}

export const getOrders = (state) => {
  const orderFilter = get(state, 'order.ordersFilter')
  const filterIsValid = ['failure', 'offline', 'success'].includes(orderFilter)
  const byId = get(state, 'order.byId', {})

  let orders = []
  if (filterIsValid) {
    const filteredIds = get(state, `order.${orderFilter}Ids`, [])
    filteredIds.forEach(id => orders.push(byId[id]))
  } else {
    orders = values(byId)
  }

  const search = get(state, 'order.ordersSearch')
  if (search) {
    orders = orders.filter(o => (o.orderNumber || '').toString().includes(search))
  }

  const sortedOrders = orderBy(orders, (o) => moment(o.submittedAt), ['desc'])
  const nonRefundable = []
  const refundable = []

  sortedOrders.forEach((order) => {
    const collection = order.refundable ? refundable : nonRefundable
    collection.push(order)
  })

  return [...nonRefundable, ...refundable]
}

const getTabOrders = (state) => {
  const byId = get(state, 'order.byId', {})
  const tabOrders = values(byId)
  const activeAttendantId = getActiveAttendantId(state)
  const showOnlyActiveAttendantOrders = getShowOnlyActiveAttendantOrders(state)
  const currentMenuUuid = getCurrentMenuId(state)

  const filteredOrders = filter(tabOrders, (order) => { 
    return (
      order.standMenuUuid === currentMenuUuid && 
      !order.customerId && 
      (!showOnlyActiveAttendantOrders || 
        order?.employeeId === activeAttendantId || 
        order?.createOrderParams?.employeeId === activeAttendantId
      )
    )
  })

  const sortedOrders = orderBy(filteredOrders, (order) => moment(order.submittedAt), ['desc'])
  const closed = []
  const open = []

  sortedOrders.forEach((order) => {
    let collection = closed
    const isMobilePay = PaymentTypes.isMobileWalletPayment(order.paymentType) || PaymentTypes.isMobileWalletPayment(order.paymentInfo?.[0]?.paymentType)

    if (OrderStates.isClosed(order.state) || isMobilePay) {
      collection = closed
    } else {
      collection = open
    }

    collection.push(order)
  })

  const showClosedTabs = getShowClosedTabs(state)
  return showClosedTabs ? [...open, ...closed] : [...open]
}

export const makeGetTabOrders = () => createSelector(
  [getTabOrders],
  (tabOrders) => tabOrders
)

const getOpenAndClosedOrdersCount = (state) => {
  const byId = state?.order?.byId ?? {}
  const tabOrders = values(byId)
  const activeAttendantId = getActiveAttendantId(state)
  const currentMenuUuid = getCurrentMenuId(state)

  const filteredOrders = filter(tabOrders, (order) => { 
    return (
      order.standMenuUuid === currentMenuUuid && 
      !order.customerId && 
      (
        order?.employeeId === activeAttendantId || 
        order?.createOrderParams?.employeeId === activeAttendantId
      )
    )
  })

  const open = []
  const closed = []

  filteredOrders.forEach((order) => {
    const isMobilePay =
      PaymentTypes.isMobileWalletPayment(order.paymentType) ||
      PaymentTypes.isMobileWalletPayment(order.paymentInfo?.[0]?.paymentType)
    const collection = OrderStates.isClosed(order.state) || isMobilePay ? closed : open
    collection.push(order)
  })

  return [open.length, closed.length]
}

export const makeGetOpenAndClosedOrdersCount = () => createSelector(
  [getOpenAndClosedOrdersCount],
  (ordersCount) => ordersCount
)

const attendantCanSeeStand = (state, standUuid) => {
  const getAttendantStandUuids = makeGetAttendantStandUuids()
  const attendantStandUuids = getAttendantStandUuids(state)

  return attendantStandUuids.includes(standUuid)
}

const getCustomerTabOrders = (state, customerId, revenueCenterId) => {
  if (!attendantCanSeeStand(state, revenueCenterId)) return []

  const byId = get(state, 'order.byId', {})
  const tabOrders = values(byId)

  const getAttendantMenuTypes = makeGetAttendantMenuTypes()
  const attendantMenuTypes = getAttendantMenuTypes(state)
  const currentEventId = getCurrentEventId(state)

  const suiteGroupTabOrders = filter(tabOrders, (order) => {
    return (
      order.customerId === customerId &&
      order.standUuid === revenueCenterId &&
      order.salesEventId === currentEventId &&
      attendantMenuTypes.includes(order.orderType)
    )
  })

  const sortedOrders = orderBy(suiteGroupTabOrders, (order) => moment(order.submittedAt), ['desc'])
  const closed = []
  const open = []

  sortedOrders.forEach((order) => {
    const collection = OrderStates.isClosed(order.state) ? closed : open
    collection.push(order)
  })

  const showClosedTabs = getShowClosedTabs(state)
  return showClosedTabs ? [...open, ...closed] : [...open]
}

export const makeGetCustomerTabOrders = () => createSelector(
  [getCustomerTabOrders],
  (customerTabOrders) => customerTabOrders
)

const getCombinableSuiteOrders = (state, customerId, revenueCenterId, destinationOrderId) => {
  const orders = getCustomerTabOrders(state, customerId, revenueCenterId)

  let combinableOrders = []
  orders.forEach((order) => {
    if (OrderStates.isOpen(order.state) && order.uuid !== destinationOrderId) {
      combinableOrders.push(order)
    }
  })

  return combinableOrders
}

export const makeGetCombinableSuiteOrders = () => createSelector(
  [getCombinableSuiteOrders],
  (combinableOrders) => combinableOrders
)

const getTabOrder = (state, orderId) => get(state, `order.byId.${orderId}`, {})

export const makeGetTabOrder = () => createSelector(
  getTabOrder,
  (tabOrder) => tabOrder
)

export const makeGetOrders = () => createSelector([getOrders], (orders) => orders)

export const getOrder = (state, id) => get(state, `order.byId.${id}`, {})

export const makeGetOrder = () => createSelector([getOrder], (order) => order)

export const getOrderCounts = (state) => {
  const failure = get(state, 'order.failureRemoteIds', [])
  const offline = get(state, 'order.unsyncedLocalIds', [])
  const offlineCardIds = get(state, 'order.offlineCardIds', [])
  const success = get(state, 'order.successRemoteIds', [])

  return {
    failure: failure.length,
    offline: offline.length + offlineCardIds.length,
    success: success.length,
  }
}

export const makeGetOrderCounts = () => createSelector([getOrderCounts], (counts) => counts)

export const getSelectedOrder = (state) => {
  const orderId = get(state, 'order.selectedOrderId')
  return get(state, `order.byId[${orderId}]`)
}

export const makeGetSelectedOrder = () => createSelector([getSelectedOrder], (order) => order)

export const getUnsyncedLocalIds = (state, limit) => {
  const result = get(state, 'order.unsyncedLocalIds', [])
  if (!isFinite(limit)) return result

  return take(result, limit)
}

export const makeGetUnsyncedLocalIds = () => createSelector([getUnsyncedLocalIds], (ids) => ids)

export const getOfflineCardIds = (state, limit) => {
  const result = get(state, 'order.offlineCardIds', [])
  if (!isFinite(limit)) return result

  return take(result, limit)
}

export const makeGetOfflineCardIds = () => createSelector([getOfflineCardIds], (ids) => ids)

export const getAllRevenueInCents = (state) => {
  const orders = values(get(state, 'order.byId', {}))
  return sumBy(orders, 'amountInCents')
}

export const makeGetAllRevenueInCents = () => createSelector([getAllRevenueInCents], (cents) => cents)

export const getFinalOrderState = (state, order ) => {
  const standUuid = !isEmpty(order) ? get(order, 'standUuid', '') : get(state, 'revenueCenter.activeRevenueCenter', 'unassigned')
  const revenueCenter = get(state, `revenueCenter.byId.${standUuid}`, {})
  const batchSettlement = get(revenueCenter, 'batchSettlement', false)
  return batchSettlement ? ORDER_STATES['COMPLETION_PENDING'] : ORDER_STATES["COMPLETED"]
}

export const makeGetServiceChargeLegalText = () => createSelector(
  makeGetOrder(),
  state => state,
  (order, state) => {
    const menuUuid = order?.standMenuUuid
    const standUuid = state.menus.byId?.[menuUuid]?.standUuid
    const stand = state.stands.byId?.[standUuid]

    return stand?.venue?.serviceChargeLegal
  }
)

export const getCurrentOrderId = (state) => state?.order?.currentOrderId ?? ''
